import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { 
  Building, 
  MapPin, 
  Info, 
  Home, 
  Ruler, 
  Calendar,
  Layers,
  Grid3X3,
  TreePine,
  Zap,
  PaintBucket,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Copy,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static dropdown options
const PROPERTY_INTEREST_OPTIONS = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'share_of_freehold', label: 'Share of Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'commonhold', label: 'Commonhold' }
];

const PROPERTY_TYPE_OPTIONS = [
  { value: 'detached_house', label: 'Detached House' },
  { value: 'semi_detached_house', label: 'Semi-Detached House' },
  { value: 'terraced_house', label: 'Terraced House' },
  { value: 'end_terrace', label: 'End Terrace' },
  { value: 'town_house', label: 'Town House' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'flat_purpose_built', label: 'Flat - Purpose Built' },
  { value: 'flat_conversion', label: 'Flat - Conversion' },
  { value: 'flat_studio', label: 'Flat - Studio' },
  { value: 'maisonette', label: 'Maisonette' },
  { value: 'maisonette_duplex', label: 'Maisonette - Duplex' },
  { value: 'maisonette_triplex', label: 'Maisonette - Triplex' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'cottage', label: 'Cottage' },
  { value: 'farmhouse', label: 'Farmhouse' },
  { value: 'barn_conversion', label: 'Barn Conversion' },
  { value: 'hmo', label: 'HMO' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed_use', label: 'Mixed Use' }
];

const ROOF_TYPE_OPTIONS = [
  { value: 'pitched', label: 'Pitched' },
  { value: 'flat', label: 'Flat' },
  { value: 'mansard', label: 'Mansard' },
  { value: 'hipped', label: 'Hipped' },
  { value: 'gable', label: 'Gable' },
  { value: 'gambrel', label: 'Gambrel' },
  { value: 'butterfly', label: 'Butterfly' },
  { value: 'dutch_gable', label: 'Dutch Gable' },
  { value: 'mono_pitched', label: 'Mono-Pitched' },
  { value: 'combination', label: 'Combination' }
];

const ROOF_COVERING_OPTIONS = [
  { value: 'slate_natural', label: 'Natural Slate' },
  { value: 'slate_reconstituted', label: 'Reconstituted Slate' },
  { value: 'tile_clay_plain', label: 'Plain Clay Tile' },
  { value: 'tile_clay_interlocking', label: 'Interlocking Clay Tile' },
  { value: 'tile_concrete_plain', label: 'Plain Concrete Tile' },
  { value: 'tile_concrete_interlocking', label: 'Interlocking Concrete Tile' },
  { value: 'felt_mineral', label: 'Mineral Felt' },
  { value: 'felt_torch_on', label: 'Torch-On Felt' },
  { value: 'asphalt', label: 'Asphalt' },
  { value: 'metal_lead', label: 'Lead' },
  { value: 'metal_zinc', label: 'Zinc' },
  { value: 'metal_copper', label: 'Copper' },
  { value: 'metal_standing_seam', label: 'Standing Seam Metal' },
  { value: 'thatch_water_reed', label: 'Water Reed Thatch' },
  { value: 'thatch_long_straw', label: 'Long Straw Thatch' },
  { value: 'grp', label: 'GRP/Fibreglass' },
  { value: 'epdm', label: 'EPDM Rubber' },
  { value: 'green_roof', label: 'Green Roof' }
];

const WALL_CONSTRUCTION_OPTIONS = [
  { value: 'solid_brick_9', label: 'Solid Brick (9 inch/225mm)' },
  { value: 'solid_brick_13', label: 'Solid Brick (13.5 inch/337mm)' },
  { value: 'solid_brick_18', label: 'Solid Brick (18 inch/450mm)' },
  { value: 'cavity_brick_brick', label: 'Cavity - Brick/Brick' },
  { value: 'cavity_brick_block', label: 'Cavity - Brick/Block' },
  { value: 'cavity_rendered', label: 'Cavity - Rendered Outer Leaf' },
  { value: 'stone_random_rubble', label: 'Stone - Random Rubble' },
  { value: 'stone_coursed', label: 'Stone - Coursed' },
  { value: 'stone_ashlar', label: 'Stone - Ashlar' },
  { value: 'timber_frame_brick', label: 'Timber Frame - Brick Clad' },
  { value: 'timber_frame_rendered', label: 'Timber Frame - Rendered' },
  { value: 'timber_frame_weatherboard', label: 'Timber Frame - Weatherboard' },
  { value: 'timber_frame_tile_hung', label: 'Timber Frame - Tile Hung' },
  { value: 'steel_frame', label: 'Steel Frame' },
  { value: 'concrete_in_situ', label: 'In-Situ Concrete' },
  { value: 'concrete_precast', label: 'Precast Concrete Panel' },
  { value: 'prefab_prc', label: 'PRC (Pre-Cast Reinforced Concrete)' },
  { value: 'prefab_wimpey_no_fines', label: 'Wimpey No-Fines' },
  { value: 'prefab_airey', label: 'Airey' },
  { value: 'prefab_reema', label: 'Reema' },
  { value: 'prefab_unity', label: 'Unity' },
  { value: 'prefab_cornish', label: 'Cornish Unit' },
  { value: 'sip_panel', label: 'SIP (Structural Insulated Panel)' },
  { value: 'icf', label: 'ICF (Insulated Concrete Form)' },
  { value: 'cob', label: 'Cob/Earth' },
  { value: 'flint', label: 'Flint' }
];

const FLOOR_CONSTRUCTION_OPTIONS = [
  { value: 'solid_concrete', label: 'Solid Concrete' },
  { value: 'solid_concrete_insulated', label: 'Solid Concrete (Insulated)' },
  { value: 'solid_stone_flags', label: 'Solid Stone Flags' },
  { value: 'solid_quarry_tiles', label: 'Solid Quarry Tiles' },
  { value: 'suspended_timber', label: 'Suspended Timber' },
  { value: 'suspended_timber_insulated', label: 'Suspended Timber (Insulated)' },
  { value: 'suspended_concrete_beam', label: 'Suspended Concrete (Beam & Block)' },
  { value: 'suspended_concrete_precast', label: 'Suspended Concrete (Precast)' },
  { value: 'mixed', label: 'Mixed (Solid & Suspended)' }
];

const WINDOW_TYPE_OPTIONS = [
  { value: 'timber_sash_single_hung', label: 'Timber Sash - Single Hung' },
  { value: 'timber_sash_double_hung', label: 'Timber Sash - Double Hung' },
  { value: 'timber_sash_box', label: 'Timber Box Sash' },
  { value: 'timber_casement_side', label: 'Timber Casement - Side Hung' },
  { value: 'timber_casement_top', label: 'Timber Casement - Top Hung' },
  { value: 'timber_tilt_turn', label: 'Timber Tilt & Turn' },
  { value: 'upvc_casement_side', label: 'uPVC Casement - Side Hung' },
  { value: 'upvc_casement_top', label: 'uPVC Casement - Top Hung' },
  { value: 'upvc_tilt_turn', label: 'uPVC Tilt & Turn' },
  { value: 'upvc_sash', label: 'uPVC Sash' },
  { value: 'upvc_bay', label: 'uPVC Bay Window' },
  { value: 'aluminium_casement', label: 'Aluminium Casement' },
  { value: 'aluminium_tilt_turn', label: 'Aluminium Tilt & Turn' },
  { value: 'aluminium_sliding', label: 'Aluminium Sliding' },
  { value: 'aluminium_bi_fold', label: 'Aluminium Bi-Fold' },
  { value: 'steel_crittall', label: 'Steel (Crittall Style)' },
  { value: 'steel_modern', label: 'Steel (Modern)' },
  { value: 'composite', label: 'Composite (Timber/Aluminium)' }
];

const GLAZING_TYPE_OPTIONS = [
  { value: 'single', label: 'Single Glazed' },
  { value: 'double_air', label: 'Double Glazed - Air Filled' },
  { value: 'double_argon', label: 'Double Glazed - Argon Filled' },
  { value: 'double_low_e', label: 'Double Glazed - Low-E' },
  { value: 'triple_argon', label: 'Triple Glazed - Argon Filled' },
  { value: 'triple_krypton', label: 'Triple Glazed - Krypton Filled' },
  { value: 'secondary', label: 'Secondary Glazing' },
  { value: 'mixed', label: 'Mixed (Various Types)' }
];

const WALL_FINISH_OPTIONS = [
  { value: 'block', label: 'Block' },
  { value: 'stud', label: 'Stud' },
  { value: 'solid', label: 'Solid' },
  { value: 'lath_plaster', label: 'Lath & Plaster' }
];

const WALL_COVERING_OPTIONS = [
  { value: 'plastered', label: 'Plastered' },
  { value: 'painted', label: 'Painted' },
  { value: 'wallpapered', label: 'Wallpapered' },
  { value: 'panelled', label: 'Panelled' },
  { value: 'exposed_brick', label: 'Exposed Brick' }
];

const FLOOR_COVERING_OPTIONS = [
  { value: 'carpet', label: 'Carpet' },
  { value: 'laminate', label: 'Laminate' },
  { value: 'hardwood', label: 'Hardwood' },
  { value: 'engineered_wood', label: 'Engineered Wood' },
  { value: 'ceramic_tiles', label: 'Ceramic Tiles' },
  { value: 'porcelain_tiles', label: 'Porcelain Tiles' },
  { value: 'vinyl', label: 'Vinyl' },
  { value: 'stone', label: 'Natural Stone' },
  { value: 'concrete', label: 'Polished Concrete' }
];

const ROAD_TYPE_OPTIONS = [
  { value: 'made_up', label: 'Made Up (Adopted)' },
  { value: 'adoption_standard', label: 'Adoption Standard' },
  { value: 'unadopted', label: 'Unadopted' },
  { value: 'private', label: 'Private Road' }
];

const LOCAL_AMENITY_OPTIONS = [
  { value: 'park', label: 'Park' },
  { value: 'train_station', label: 'Train Station' },
  { value: 'tube_station', label: 'Tube Station' },
  { value: 'bus_stop', label: 'Bus Stop' },
  { value: 'shops', label: 'Shops' },
  { value: 'supermarket', label: 'Supermarket' },
  { value: 'school_primary', label: 'Primary School' },
  { value: 'school_secondary', label: 'Secondary School' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'gp_surgery', label: 'GP Surgery' },
  { value: 'fields', label: 'Fields/Open Space' },
  { value: 'gym', label: 'Gym/Leisure Centre' },
  { value: 'restaurant', label: 'Restaurants' }
];

const UTILITY_OPTIONS = [
  { value: 'gas_mains', label: 'Mains Gas' },
  { value: 'electric_mains', label: 'Mains Electric' },
  { value: 'water_mains', label: 'Mains Water' },
  { value: 'drainage_mains', label: 'Mains Drainage' },
  { value: 'fiber_broadband', label: 'Fibre Broadband' },
  { value: 'solar_panels', label: 'Solar Panels' },
  { value: 'ev_charging', label: 'EV Charging' }
];

const ROOM_TYPES = [
  'Kitchen', 'Kitchen/Diner', 'Kitchen/Breakfast', 'Reception', 'Living Room', 
  'Lounge', 'Sitting Room', 'Drawing Room', 'Bedroom', 'Master Bedroom',
  'Double Bedroom', 'Single Bedroom', 'Box Room', 'Bathroom', 'Shower Room',
  'En-Suite', 'WC', 'Cloakroom', 'Utility', 'Utility/WC', 'Dining Room', 
  'Dining Kitchen', 'Study', 'Home Office', 'Conservatory', 'Orangery',
  'Basement', 'Cellar', 'Loft Room', 'Attic Room', 'Garage', 'Integral Garage',
  'Store Room', 'Boot Room', 'Pantry', 'Laundry', 'Gym', 'Games Room',
  'Media Room', 'Wine Cellar', 'Plant Room', 'Commercial Unit', 'Shop',
  'Office', 'Workshop', 'Hall', 'Landing', 'Entrance Vestibule'
];

// Generate age options with specific periods
const AGE_OPTIONS = [
  { value: 'pre_1850', label: 'Pre-1850 (Georgian/Earlier)' },
  { value: '1850_1899', label: '1850-1899 (Victorian)' },
  { value: '1900_1918', label: '1900-1918 (Edwardian)' },
  { value: '1919_1929', label: '1919-1929 (Inter-War Early)' },
  { value: '1930_1939', label: '1930-1939 (Inter-War Late)' },
  { value: '1940_1949', label: '1940-1949 (WWII Era)' },
  { value: '1950_1959', label: '1950-1959 (Post-War)' },
  { value: '1960_1969', label: '1960-1969' },
  { value: '1970_1979', label: '1970-1979' },
  { value: '1980_1989', label: '1980-1989' },
  { value: '1990_1999', label: '1990-1999' },
  { value: '2000_2009', label: '2000-2009' },
  { value: '2010_2019', label: '2010-2019' },
  { value: '2020_2024', label: '2020-2024' },
  { value: '2025_onwards', label: '2025 Onwards (New Build)' },
  { value: 'unknown', label: 'Unknown' }
];

// Generate floor level options 0-50
const FLOOR_LEVEL_OPTIONS = Array.from({ length: 51 }, (_, i) => ({
  value: i.toString(),
  label: i === 0 ? 'Ground' : i === 1 ? '1st' : i === 2 ? '2nd' : i === 3 ? '3rd' : `${i}th`
}));

// Generate stories options 1-50
const STORIES_OPTIONS = Array.from({ length: 50 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString()
}));

export default function PropertySetupForm({ propertyDetails, onChange }) {
  const { token } = useContext(AuthContext);
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    address: true,
    specifications: true,
    construction: false,
    accommodation: false,
    amenities: false,
    finishes: false,
    comparables: false
  });

  // Area unit toggle - 'sqm' for square metres, 'sqft' for square feet
  const [areaUnit, setAreaUnit] = useState('sqm');
  
  // Conversion factors
  const SQM_TO_SQFT = 10.7639;
  const SQFT_TO_SQM = 0.092903;

  // Convert area for display
  const displayArea = (sqmValue) => {
    if (!sqmValue && sqmValue !== 0) return '';
    if (areaUnit === 'sqft') {
      return Math.round(sqmValue * SQM_TO_SQFT * 100) / 100;
    }
    return sqmValue;
  };

  // Convert input back to sqm for storage
  const parseAreaInput = (inputValue) => {
    if (!inputValue && inputValue !== 0) return null;
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) return null;
    if (areaUnit === 'sqft') {
      return Math.round(numValue * SQFT_TO_SQM * 100) / 100;
    }
    return numValue;
  };

  const getAreaLabel = () => areaUnit === 'sqm' ? 'm²' : 'ft²';

  useEffect(() => {
    fetchAllLookups();
  }, []);

  const fetchAllLookups = async () => {
    setLoading(true);
    try {
      const lookupTypes = [
        'property_types',
        'tenures',
        'construction_types',
        'building_ages',
        'energy_ratings',
        'occupancy_statuses',
        'property_statuses',
        'usage_types'
      ];

      const responses = await Promise.all(
        lookupTypes.map(type => axios.get(`${API}/lookups/${type}`).catch(() => ({ data: [] })))
      );

      const lookupsData = {};
      lookupTypes.forEach((type, index) => {
        lookupsData[type] = responses[index].data;
      });

      setLookups(lookupsData);
    } catch (error) {
      toast.error('Failed to load dropdown options');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    onChange({ ...propertyDetails, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    onChange({
      ...propertyDetails,
      [parent]: {
        ...(propertyDetails?.[parent] || {}),
        [field]: value
      }
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleAmenityToggle = (amenity) => {
    const current = propertyDetails?.local_amenities || [];
    const updated = current.includes(amenity)
      ? current.filter(a => a !== amenity)
      : [...current, amenity];
    handleChange('local_amenities', updated);
  };

  const handleUtilityToggle = (utility) => {
    const current = propertyDetails?.utilities || [];
    const updated = current.includes(utility)
      ? current.filter(u => u !== utility)
      : [...current, utility];
    handleChange('utilities', updated);
  };

  // Accommodation Matrix handlers
  const addRoom = () => {
    const rooms = propertyDetails?.accommodation || [];
    handleChange('accommodation', [...rooms, {
      id: Date.now(),
      type: 'Bedroom',
      floor: '0',
      count: 1,
      length_m: null,
      width_m: null,
      area_sqm: null,
      is_commercial: false,
      has_en_suite: false,
      aspect: '',
      notes: ''
    }]);
  };

  const updateRoom = (index, field, value) => {
    const rooms = [...(propertyDetails?.accommodation || [])];
    rooms[index] = { ...rooms[index], [field]: value };
    
    // Auto-calculate area if length and width are provided
    if (field === 'length_m' || field === 'width_m') {
      const length = field === 'length_m' ? value : rooms[index].length_m;
      const width = field === 'width_m' ? value : rooms[index].width_m;
      if (length && width && length > 0 && width > 0) {
        rooms[index].area_sqm = Math.round(length * width * 100) / 100;
      }
    }
    
    handleChange('accommodation', rooms);
  };

  const duplicateRoom = (index) => {
    const rooms = [...(propertyDetails?.accommodation || [])];
    const roomToCopy = { ...rooms[index], id: Date.now() };
    rooms.splice(index + 1, 0, roomToCopy);
    handleChange('accommodation', rooms);
  };

  const moveRoom = (index, direction) => {
    const rooms = [...(propertyDetails?.accommodation || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rooms.length) return;
    [rooms[index], rooms[newIndex]] = [rooms[newIndex], rooms[index]];
    handleChange('accommodation', rooms);
  };

  const removeRoom = (index) => {
    const rooms = [...(propertyDetails?.accommodation || [])];
    rooms.splice(index, 1);
    handleChange('accommodation', rooms);
  };

  // Comparables handlers
  const addComparable = () => {
    const comparables = propertyDetails?.comparables || [];
    handleChange('comparables', [...comparables, {
      id: Date.now(),
      address: '',
      distance_miles: null,
      property_type: '',
      age: '',
      bedrooms: null,
      receptions: null,
      bathrooms: null,
      floor_area_sqm: null,
      location_comparison: 'similar',
      size_comparison: 'similar',
      condition_comparison: 'similar',
      sale_date: '',
      sale_type: 'sold',
      sale_price: null,
      price_per_sqm: null,
      agent: '',
      adjusted_valuation: null
    }]);
  };

  const updateComparable = (index, field, value) => {
    const comparables = [...(propertyDetails?.comparables || [])];
    comparables[index] = { ...comparables[index], [field]: value };
    
    // Auto-calculate price per sqm
    if (field === 'sale_price' || field === 'floor_area_sqm') {
      const price = field === 'sale_price' ? value : comparables[index].sale_price;
      const area = field === 'floor_area_sqm' ? value : comparables[index].floor_area_sqm;
      if (price && area && area > 0) {
        comparables[index].price_per_sqm = Math.round(price / area);
      }
    }
    
    handleChange('comparables', comparables);
  };

  const removeComparable = (index) => {
    const comparables = [...(propertyDetails?.comparables || [])];
    comparables.splice(index, 1);
    handleChange('comparables', comparables);
  };

  const showLeaseFields = () => {
    const interest = propertyDetails?.property_interest;
    return interest === 'leasehold' || interest === 'share_of_freehold';
  };

  const SectionHeader = ({ title, icon: Icon, section, description }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors rounded-t-lg"
    >
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-accent" />
        <div className="text-left">
          <h3 className="font-semibold">{title}</h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="h-5 w-5 text-muted-foreground" />
      ) : (
        <ChevronDown className="h-5 w-5 text-muted-foreground" />
      )}
    </button>
  );

  if (loading) return <div className="text-center py-8">Loading property fields...</div>;

  return (
    <div className="space-y-4" data-testid="property-setup-form">
      {/* Section 1: Header & Core Details */}
      <Card>
        <SectionHeader 
          title="Address & Core Details" 
          icon={MapPin} 
          section="address"
          description="Property address and ownership details"
        />
        {expandedSections.address && (
          <CardContent className="space-y-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="address">Full Address *</Label>
                <Textarea
                  id="address"
                  value={propertyDetails?.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Enter full property address including postcode"
                  rows={2}
                  data-testid="full-address-input"
                />
              </div>
              
              <div>
                <Label htmlFor="property_interest">Property Interest *</Label>
                <Select
                  value={propertyDetails?.property_interest || ''}
                  onValueChange={(value) => handleChange('property_interest', value)}
                >
                  <SelectTrigger data-testid="property-interest-select">
                    <SelectValue placeholder="Select interest type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_INTEREST_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="valuation_date">Valuation Date *</Label>
                <Input
                  id="valuation_date"
                  type="date"
                  value={propertyDetails?.valuation_date || ''}
                  onChange={(e) => handleChange('valuation_date', e.target.value)}
                  data-testid="valuation-date-input"
                />
              </div>
            </div>

            {/* Conditional Leasehold Fields */}
            {showLeaseFields() && (
              <>
                <Separator className="my-4" />
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Leasehold Details</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="lease_length">Lease Length (years)</Label>
                      <Input
                        id="lease_length"
                        type="number"
                        value={propertyDetails?.lease_length_years || ''}
                        onChange={(e) => handleChange('lease_length_years', parseInt(e.target.value) || null)}
                        placeholder="99"
                        data-testid="lease-length-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ground_rent">Ground Rent (£/year)</Label>
                      <Input
                        id="ground_rent"
                        type="number"
                        step="0.01"
                        value={propertyDetails?.ground_rent_annual || ''}
                        onChange={(e) => handleChange('ground_rent_annual', parseFloat(e.target.value) || null)}
                        placeholder="250"
                        data-testid="ground-rent-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service_charge">Service Charge (£/year)</Label>
                      <Input
                        id="service_charge"
                        type="number"
                        step="0.01"
                        value={propertyDetails?.service_charge_annual || ''}
                        onChange={(e) => handleChange('service_charge_annual', parseFloat(e.target.value) || null)}
                        placeholder="1200"
                        data-testid="service-charge-input"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Section 2: Property Specifications */}
      <Card>
        <SectionHeader 
          title="Property Specifications" 
          icon={Building} 
          section="specifications"
          description="Type, size, and structural details"
        />
        {expandedSections.specifications && (
          <CardContent className="space-y-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="property_type">Property Type *</Label>
                <Select
                  value={propertyDetails?.property_type || ''}
                  onValueChange={(value) => handleChange('property_type', value)}
                >
                  <SelectTrigger data-testid="property-type-select">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="floor_level">Floor Level</Label>
                <Select
                  value={propertyDetails?.floor_level || ''}
                  onValueChange={(value) => handleChange('floor_level', value)}
                >
                  <SelectTrigger data-testid="floor-level-select">
                    <SelectValue placeholder="Select floor" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLOOR_LEVEL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="stories">Number of Stories</Label>
                <Select
                  value={propertyDetails?.stories || ''}
                  onValueChange={(value) => handleChange('stories', value)}
                >
                  <SelectTrigger data-testid="stories-select">
                    <SelectValue placeholder="Select stories" />
                  </SelectTrigger>
                  <SelectContent>
                    {STORIES_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="number_of_units">Number of Units</Label>
                <Input
                  id="number_of_units"
                  type="number"
                  value={propertyDetails?.number_of_units || ''}
                  onChange={(e) => handleChange('number_of_units', parseInt(e.target.value) || null)}
                  placeholder="1"
                  data-testid="units-input"
                />
              </div>
              
              <div>
                <Label htmlFor="age">Building Age</Label>
                <Select
                  value={propertyDetails?.age || ''}
                  onValueChange={(value) => handleChange('age', value)}
                >
                  <SelectTrigger data-testid="age-select">
                    <SelectValue placeholder="Select decade" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="gross_internal_area">Gross Internal Area ({getAreaLabel()})</Label>
                  <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                    <button
                      type="button"
                      onClick={() => setAreaUnit('sqm')}
                      className={`px-2 py-1 text-xs rounded-md transition-colors ${
                        areaUnit === 'sqm' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="area-unit-sqm"
                    >
                      m²
                    </button>
                    <button
                      type="button"
                      onClick={() => setAreaUnit('sqft')}
                      className={`px-2 py-1 text-xs rounded-md transition-colors ${
                        areaUnit === 'sqft' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="area-unit-sqft"
                    >
                      ft²
                    </button>
                  </div>
                </div>
                <Input
                  id="gross_internal_area"
                  type="number"
                  step="0.01"
                  value={displayArea(propertyDetails?.gross_internal_area) || ''}
                  onChange={(e) => handleChange('gross_internal_area', parseAreaInput(e.target.value))}
                  placeholder={areaUnit === 'sqm' ? '85.5' : '920'}
                  data-testid="gia-input"
                />
                {propertyDetails?.gross_internal_area && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {areaUnit === 'sqm' 
                      ? `≈ ${Math.round(propertyDetails.gross_internal_area * SQM_TO_SQFT)} ft²`
                      : `≈ ${propertyDetails.gross_internal_area} m²`
                    }
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 3: Construction Details */}
      <Card>
        <SectionHeader 
          title="Construction Details" 
          icon={Layers} 
          section="construction"
          description="Roof, walls, and windows"
        />
        {expandedSections.construction && (
          <CardContent className="space-y-6 pt-0">
            {/* Roof */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Roof</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="roof_type">Roof Type</Label>
                  <Select
                    value={propertyDetails?.construction?.roof_type || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'roof_type', value)}
                  >
                    <SelectTrigger data-testid="roof-type-select">
                      <SelectValue placeholder="Select roof type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOF_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="roof_covering">Roof Covering</Label>
                  <Select
                    value={propertyDetails?.construction?.roof_covering || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'roof_covering', value)}
                  >
                    <SelectTrigger data-testid="roof-covering-select">
                      <SelectValue placeholder="Select covering" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOF_COVERING_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Walls */}
            <div>
              <Label className="text-sm font-medium mb-3 block">External Walls</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="wall_construction">Wall Construction</Label>
                  <Select
                    value={propertyDetails?.construction?.wall_construction || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'wall_construction', value)}
                  >
                    <SelectTrigger data-testid="wall-construction-select">
                      <SelectValue placeholder="Select construction" />
                    </SelectTrigger>
                    <SelectContent>
                      {WALL_CONSTRUCTION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Floors */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Floor Construction</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="floor_construction">Ground Floor</Label>
                  <Select
                    value={propertyDetails?.construction?.floor_construction || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'floor_construction', value)}
                  >
                    <SelectTrigger data-testid="floor-construction-select">
                      <SelectValue placeholder="Select floor type" />
                    </SelectTrigger>
                    <SelectContent>
                      {FLOOR_CONSTRUCTION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="upper_floor_construction">Upper Floors</Label>
                  <Select
                    value={propertyDetails?.construction?.upper_floor_construction || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'upper_floor_construction', value)}
                  >
                    <SelectTrigger data-testid="upper-floor-construction-select">
                      <SelectValue placeholder="Select floor type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="suspended_timber">Suspended Timber</SelectItem>
                      <SelectItem value="suspended_timber_insulated">Suspended Timber (Insulated)</SelectItem>
                      <SelectItem value="suspended_concrete_beam">Suspended Concrete (Beam & Block)</SelectItem>
                      <SelectItem value="suspended_concrete_precast">Suspended Concrete (Precast)</SelectItem>
                      <SelectItem value="not_applicable">N/A (Single Storey)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Windows */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Windows</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="window_type">Window Type</Label>
                  <Select
                    value={propertyDetails?.construction?.window_type || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'window_type', value)}
                  >
                    <SelectTrigger data-testid="window-type-select">
                      <SelectValue placeholder="Select window type" />
                    </SelectTrigger>
                    <SelectContent>
                      {WINDOW_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="glazing_type">Glazing Type</Label>
                  <Select
                    value={propertyDetails?.construction?.glazing_type || ''}
                    onValueChange={(value) => handleNestedChange('construction', 'glazing_type', value)}
                  >
                    <SelectTrigger data-testid="glazing-type-select">
                      <SelectValue placeholder="Select glazing" />
                    </SelectTrigger>
                    <SelectContent>
                      {GLAZING_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 4: Accommodation Matrix */}
      <Card>
        <SectionHeader 
          title="Accommodation Schedule" 
          icon={Grid3X3} 
          section="accommodation"
          description="Room breakdown and floor plan"
        />
        {expandedSections.accommodation && (
          <CardContent className="space-y-4 pt-0">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">
                Add rooms to create an accommodation schedule
              </p>
              <Button type="button" onClick={addRoom} size="sm" data-testid="add-room-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Room
              </Button>
            </div>

            {(propertyDetails?.accommodation || []).length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-40">Room Type</TableHead>
                      <TableHead className="w-24">Floor</TableHead>
                      <TableHead className="w-20">Length (m)</TableHead>
                      <TableHead className="w-20">Width (m)</TableHead>
                      <TableHead className="w-20">Area ({getAreaLabel()})</TableHead>
                      <TableHead className="w-24">Aspect</TableHead>
                      <TableHead className="w-16 text-center">En-Suite</TableHead>
                      <TableHead className="w-16 text-center">Comm.</TableHead>
                      <TableHead className="w-32">Notes</TableHead>
                      <TableHead className="w-28 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(propertyDetails?.accommodation || []).map((room, index) => (
                      <TableRow key={room.id || index} data-testid={`accommodation-row-${index}`}>
                        <TableCell>
                          <Select
                            value={room.type}
                            onValueChange={(value) => updateRoom(index, 'type', value)}
                          >
                            <SelectTrigger className="w-36" data-testid={`room-${index}-type-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROOM_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={room.floor}
                            onValueChange={(value) => updateRoom(index, 'floor', value)}
                          >
                            <SelectTrigger className="w-20" data-testid={`room-${index}-floor-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FLOOR_LEVEL_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="50"
                            value={room.length_m || ''}
                            onChange={(e) => updateRoom(index, 'length_m', parseFloat(e.target.value) || null)}
                            className="w-16"
                            placeholder="L"
                            data-testid={`room-${index}-length-input`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="50"
                            value={room.width_m || ''}
                            onChange={(e) => updateRoom(index, 'width_m', parseFloat(e.target.value) || null)}
                            className="w-16"
                            placeholder="W"
                            data-testid={`room-${index}-width-input`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={displayArea(room.area_sqm) || ''}
                            onChange={(e) => updateRoom(index, 'area_sqm', parseAreaInput(e.target.value))}
                            className="w-16 bg-muted/50"
                            placeholder="Auto"
                            data-testid={`room-${index}-area-input`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={room.aspect || ''}
                            onValueChange={(value) => updateRoom(index, 'aspect', value)}
                          >
                            <SelectTrigger className="w-20" data-testid={`room-${index}-aspect-select`}>
                              <SelectValue placeholder="-" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="north">North</SelectItem>
                              <SelectItem value="south">South</SelectItem>
                              <SelectItem value="east">East</SelectItem>
                              <SelectItem value="west">West</SelectItem>
                              <SelectItem value="north_east">NE</SelectItem>
                              <SelectItem value="north_west">NW</SelectItem>
                              <SelectItem value="south_east">SE</SelectItem>
                              <SelectItem value="south_west">SW</SelectItem>
                              <SelectItem value="dual">Dual</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={room.has_en_suite || false}
                            onCheckedChange={(checked) => updateRoom(index, 'has_en_suite', checked)}
                            data-testid={`room-${index}-ensuite-checkbox`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={room.is_commercial || false}
                            onCheckedChange={(checked) => updateRoom(index, 'is_commercial', checked)}
                            data-testid={`room-${index}-commercial-checkbox`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={room.notes || ''}
                            onChange={(e) => updateRoom(index, 'notes', e.target.value)}
                            className="w-28"
                            placeholder="Notes"
                            data-testid={`room-${index}-notes-input`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => moveRoom(index, 'up')}
                              disabled={index === 0}
                              className="h-7 w-7"
                              title="Move up"
                              data-testid={`room-${index}-move-up-btn`}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => moveRoom(index, 'down')}
                              disabled={index === (propertyDetails?.accommodation || []).length - 1}
                              className="h-7 w-7"
                              title="Move down"
                              data-testid={`room-${index}-move-down-btn`}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => duplicateRoom(index)}
                              className="h-7 w-7"
                              title="Duplicate"
                              data-testid={`room-${index}-duplicate-btn`}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeRoom(index)}
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              title="Delete"
                              data-testid={`room-${index}-delete-btn`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                No rooms added. Click "Add Room" to start.
              </div>
            )}

            {/* Summary */}
            {(propertyDetails?.accommodation || []).length > 0 && (
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Badge variant="outline">
                  Bedrooms: {(propertyDetails?.accommodation || [])
                    .filter(r => r.type?.toLowerCase().includes('bedroom'))
                    .length}
                </Badge>
                <Badge variant="outline">
                  Receptions: {(propertyDetails?.accommodation || [])
                    .filter(r => ['Reception', 'Living Room', 'Lounge', 'Sitting Room', 'Drawing Room'].includes(r.type))
                    .length}
                </Badge>
                <Badge variant="outline">
                  Bathrooms: {(propertyDetails?.accommodation || [])
                    .filter(r => ['Bathroom', 'Shower Room', 'En-Suite', 'WC', 'Cloakroom'].includes(r.type))
                    .length}
                </Badge>
                <Badge variant="outline">
                  Total Rooms: {(propertyDetails?.accommodation || []).length}
                </Badge>
                <Badge variant="secondary">
                  Total Area: {displayArea((propertyDetails?.accommodation || [])
                    .reduce((sum, r) => sum + (r.area_sqm || 0), 0))} {getAreaLabel()}
                </Badge>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Section 5: Amenities & Services */}
      <Card>
        <SectionHeader 
          title="Amenities & Services" 
          icon={TreePine} 
          section="amenities"
          description="Local amenities, utilities, and roads"
        />
        {expandedSections.amenities && (
          <CardContent className="space-y-6 pt-0">
            {/* Local Amenities */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Local Amenities</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {LOCAL_AMENITY_OPTIONS.map((amenity) => (
                  <div key={amenity.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`amenity-${amenity.value}`}
                      checked={(propertyDetails?.local_amenities || []).includes(amenity.value)}
                      onCheckedChange={() => handleAmenityToggle(amenity.value)}
                    />
                    <Label htmlFor={`amenity-${amenity.value}`} className="cursor-pointer text-sm">
                      {amenity.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Utilities */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Utilities Available</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {UTILITY_OPTIONS.map((utility) => (
                  <div key={utility.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`utility-${utility.value}`}
                      checked={(propertyDetails?.utilities || []).includes(utility.value)}
                      onCheckedChange={() => handleUtilityToggle(utility.value)}
                    />
                    <Label htmlFor={`utility-${utility.value}`} className="cursor-pointer text-sm">
                      {utility.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Roads */}
            <div>
              <Label htmlFor="road_type">Road Type</Label>
              <Select
                value={propertyDetails?.road_type || ''}
                onValueChange={(value) => handleChange('road_type', value)}
              >
                <SelectTrigger className="w-64" data-testid="road-type-select">
                  <SelectValue placeholder="Select road type" />
                </SelectTrigger>
                <SelectContent>
                  {ROAD_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 6: Internal Finishes */}
      <Card>
        <SectionHeader 
          title="Internal Finishes" 
          icon={PaintBucket} 
          section="finishes"
          description="Walls, ceilings, and floor finishes"
        />
        {expandedSections.finishes && (
          <CardContent className="space-y-6 pt-0">
            {/* Walls & Ceilings */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Walls & Ceilings</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="wall_finish">Wall Construction</Label>
                  <Select
                    value={propertyDetails?.finishes?.wall_finish || ''}
                    onValueChange={(value) => handleNestedChange('finishes', 'wall_finish', value)}
                  >
                    <SelectTrigger data-testid="wall-finish-select">
                      <SelectValue placeholder="Select finish" />
                    </SelectTrigger>
                    <SelectContent>
                      {WALL_FINISH_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="wall_covering">Wall Covering</Label>
                  <Select
                    value={propertyDetails?.finishes?.wall_covering || ''}
                    onValueChange={(value) => handleNestedChange('finishes', 'wall_covering', value)}
                  >
                    <SelectTrigger data-testid="wall-covering-select">
                      <SelectValue placeholder="Select covering" />
                    </SelectTrigger>
                    <SelectContent>
                      {WALL_COVERING_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Floors */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Floors</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="floor_construction">Floor Construction</Label>
                  <Select
                    value={propertyDetails?.finishes?.floor_construction || ''}
                    onValueChange={(value) => handleNestedChange('finishes', 'floor_construction', value)}
                  >
                    <SelectTrigger data-testid="floor-construction-select">
                      <SelectValue placeholder="Select construction" />
                    </SelectTrigger>
                    <SelectContent>
                      {FLOOR_CONSTRUCTION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="floor_covering">Primary Floor Covering</Label>
                  <Select
                    value={propertyDetails?.finishes?.floor_covering || ''}
                    onValueChange={(value) => handleNestedChange('finishes', 'floor_covering', value)}
                  >
                    <SelectTrigger data-testid="floor-covering-select">
                      <SelectValue placeholder="Select covering" />
                    </SelectTrigger>
                    <SelectContent>
                      {FLOOR_COVERING_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 7: Residential Comparables */}
      <Card>
        <SectionHeader 
          title="Residential Comparables" 
          icon={Ruler} 
          section="comparables"
          description="Comparable properties for valuation"
        />
        {expandedSections.comparables && (
          <CardContent className="space-y-4 pt-0">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">
                Add comparable properties to support valuation
              </p>
              <Button type="button" onClick={addComparable} size="sm" data-testid="add-comparable-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Comparable
              </Button>
            </div>

            {(propertyDetails?.comparables || []).length > 0 ? (
              <div className="space-y-4">
                {(propertyDetails?.comparables || []).map((comp, index) => (
                  <Card key={comp.id || index} className="p-4 bg-muted/30">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium">Comparable {index + 1}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeComparable(index)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-comparable-${index}-btn`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="md:col-span-2">
                        <Label>Address</Label>
                        <Input
                          value={comp.address || ''}
                          onChange={(e) => updateComparable(index, 'address', e.target.value)}
                          placeholder="Property address"
                        />
                      </div>
                      <div>
                        <Label>Distance (miles)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={comp.distance_miles || ''}
                          onChange={(e) => updateComparable(index, 'distance_miles', parseFloat(e.target.value) || null)}
                          placeholder="0.5"
                        />
                      </div>
                      <div>
                        <Label>Type</Label>
                        <Select
                          value={comp.property_type || ''}
                          onValueChange={(value) => updateComparable(index, 'property_type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROPERTY_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Age</Label>
                        <Select
                          value={comp.age || ''}
                          onValueChange={(value) => updateComparable(index, 'age', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Age" />
                          </SelectTrigger>
                          <SelectContent>
                            {AGE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Beds</Label>
                        <Input
                          type="number"
                          value={comp.bedrooms || ''}
                          onChange={(e) => updateComparable(index, 'bedrooms', parseInt(e.target.value) || null)}
                          placeholder="3"
                        />
                      </div>
                      <div>
                        <Label>Rec</Label>
                        <Input
                          type="number"
                          value={comp.receptions || ''}
                          onChange={(e) => updateComparable(index, 'receptions', parseInt(e.target.value) || null)}
                          placeholder="2"
                        />
                      </div>
                      <div>
                        <Label>Ba/Sh</Label>
                        <Input
                          type="number"
                          value={comp.bathrooms || ''}
                          onChange={(e) => updateComparable(index, 'bathrooms', parseInt(e.target.value) || null)}
                          placeholder="2"
                        />
                      </div>
                      <div>
                        <Label>Floor Area ({getAreaLabel()})</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={displayArea(comp.floor_area_sqm) || ''}
                          onChange={(e) => updateComparable(index, 'floor_area_sqm', parseAreaInput(e.target.value))}
                          placeholder={areaUnit === 'sqm' ? '85' : '915'}
                        />
                      </div>
                    </div>

                    <Separator className="my-4" />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Location</Label>
                        <Select
                          value={comp.location_comparison || 'similar'}
                          onValueChange={(value) => updateComparable(index, 'location_comparison', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="superior">Superior</SelectItem>
                            <SelectItem value="similar">Similar</SelectItem>
                            <SelectItem value="inferior">Inferior</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Size</Label>
                        <Select
                          value={comp.size_comparison || 'similar'}
                          onValueChange={(value) => updateComparable(index, 'size_comparison', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="larger">Larger</SelectItem>
                            <SelectItem value="similar">Similar</SelectItem>
                            <SelectItem value="smaller">Smaller</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Condition</Label>
                        <Select
                          value={comp.condition_comparison || 'similar'}
                          onValueChange={(value) => updateComparable(index, 'condition_comparison', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="better">Better</SelectItem>
                            <SelectItem value="similar">Similar</SelectItem>
                            <SelectItem value="worse">Worse</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Separator className="my-4" />

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <Label>Sale Date</Label>
                        <Input
                          type="date"
                          value={comp.sale_date || ''}
                          onChange={(e) => updateComparable(index, 'sale_date', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select
                          value={comp.sale_type || 'sold'}
                          onValueChange={(value) => updateComparable(index, 'sale_type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sold">Sold</SelectItem>
                            <SelectItem value="for_sale">For Sale</SelectItem>
                            <SelectItem value="under_offer">Under Offer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Sale Price (£)</Label>
                        <Input
                          type="number"
                          value={comp.sale_price || ''}
                          onChange={(e) => updateComparable(index, 'sale_price', parseFloat(e.target.value) || null)}
                          placeholder="450000"
                        />
                      </div>
                      <div>
                        <Label>Rate (£/m²)</Label>
                        <Input
                          type="number"
                          value={comp.price_per_sqm || ''}
                          readOnly
                          className="bg-muted"
                          placeholder="Auto"
                        />
                      </div>
                      <div>
                        <Label>Agent</Label>
                        <Input
                          value={comp.agent || ''}
                          onChange={(e) => updateComparable(index, 'agent', e.target.value)}
                          placeholder="Agent name"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label>Adjusted Valuation (£)</Label>
                      <Input
                        type="number"
                        value={comp.adjusted_valuation || ''}
                        onChange={(e) => updateComparable(index, 'adjusted_valuation', parseFloat(e.target.value) || null)}
                        placeholder="Enter adjusted value"
                        className="w-48"
                      />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                No comparables added. Click "Add Comparable" to start.
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
